import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  Optional,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';

import { EditorToolbarComponent } from '../ui-editor-toolbar/ui-editor-toolbar.component';
import { EditorLinkPopoverComponent } from '../ui-editor-link-dialog/ui-editor-link-popover.component';
import { ContentLinkDropdownComponent } from '../content-link-dropdown/content-link-dropdown.component';
import { EditorFormatState } from '../../types';
import { MarkdownEditorService } from '../../services/markdown-editor.service';
import { ContentLinkMenuOpen } from '../../plugins/content-link.plugin';
import {
  EDITOR_CONTENT_LINK_SEARCH,
  EditorContentLinkTarget
} from '../../content-link-search.token';

@Component({
  selector: 'bl-ui-markdown-editor',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    ReactiveFormsModule,
    EditorToolbarComponent,
    EditorLinkPopoverComponent,
    ContentLinkDropdownComponent
  ],
  template: `
    <div>
      <bl-editor-toolbar [formatState]="formatState"></bl-editor-toolbar>
      <hr />

      <div
        class="editor-content"
        contenteditable="true"
        #contenteditable
        (click)="onEditorInteraction()"
        (keyup)="onEditorInteraction()"
        [attr.aria-label]="ariaLabel"
        [attr.data-placeholder]="placeholder"
      ></div>

      <bl-editor-link-popover></bl-editor-link-popover>

      <bl-content-link-dropdown
        *ngIf="contentLinkMenu"
        [anchorRect]="contentLinkMenu.anchorRect"
        [targets]="contentLinkTargets"
        [loading]="contentLinkLoading"
        (targetSelected)="insertContentLink($event)"
      ></bl-content-link-dropdown>
    </div>
  `,
  styleUrls: ['./ui-markdown-editor.component.scss'],
  providers: [
    MarkdownEditorService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiMarkdownEditorComponent),
      multi: true
    }
  ]
})
export class UiMarkdownEditorComponent
  implements AfterViewInit, OnDestroy, ControlValueAccessor
{
  @ViewChild('contenteditable') contenteditable?: ElementRef<HTMLDivElement>;

  @Input() buttonSize: 'small' | 'default' | 'large' = 'default';
  @Input() ariaLabel = 'Markdown editor';
  @Input() placeholder = 'Start typing here...';

  formatState: EditorFormatState;
  contentLinkMenu: ContentLinkMenuOpen | null = null;
  contentLinkTargets: EditorContentLinkTarget[] = [];
  contentLinkLoading = false;

  protected ngUnsubscribe = new Subject<void>();

  private onChange: (value: string) => void = () => {
    //   No-op
  };
  private onTouch: () => void = () => {
    //   No-op
  };

  constructor(
    private editorService: MarkdownEditorService,
    @Optional()
    @Inject(EDITOR_CONTENT_LINK_SEARCH)
    private contentLinkSearch:
      | ((query: string) => Observable<EditorContentLinkTarget[]>)
      | null
  ) {
    this.formatState = this.editorService.getFormatState();
  }

  writeValue(value: string): void {
    this.editorService.updateContent(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  ngAfterViewInit() {
    if (this.contenteditable) {
      this.editorService.initializeEditor(this.contenteditable.nativeElement);

      this.editorService.formatState$
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(formatState => {
          this.formatState = formatState;

          const currentValue = this.editorService.getValue();
          this.onChange(currentValue);
        });

      this.contenteditable.nativeElement.addEventListener('blur', () => {
        this.onTouch();
      });

      this.setupContentLinkTrigger();
    }
  }

  // practically never called in ionic because of caching but leave it as it is until it makes problems
  ngOnDestroy() {
    this.editorService.destroyEditor();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onEditorInteraction() {
    this.onTouch();
  }

  insertContentLink(target: EditorContentLinkTarget): void {
    this.editorService.insertContentLink({
      title: target.title,
      url: target.url
    });
  }

  private setupContentLinkTrigger(): void {
    const search = this.contentLinkSearch;
    if (!search) return;

    const menuState$ = this.editorService.registerContentLinkTrigger();

    menuState$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(state => {
      this.contentLinkMenu = state === 'closed' ? null : state;
      if (state === 'closed') {
        this.contentLinkTargets = [];
      }
    });

    menuState$
      .pipe(
        map(state => (state === 'closed' ? null : state.query)),
        distinctUntilChanged(),
        tap(() => (this.contentLinkLoading = true)),
        debounceTime(200),
        switchMap(query => (query === null ? of([]) : search(query))),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(targets => {
        this.contentLinkTargets = targets;
        this.contentLinkLoading = false;
      });
  }
}
